import { useState, useEffect, useRef, Fragment } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, STATUS_COLORS } from '../utils/api'
import { Asset, AssetsResponse } from '../types'
import DataSourceBadge from '../components/DataSourceBadge'
import QRCodeModal from '../components/QRCodeModal'
import { Plus, Search, Download, Package, ChevronUp, ChevronDown, ChevronsUpDown, Printer, ClipboardList, Trash2, Paperclip, X, File, ImageIcon, Upload, RefreshCw, ExternalLink, Wrench, ArrowLeftRight, Network, Columns3, QrCode, ArrowUp, ArrowDown, RotateCcw } from 'lucide-react'
import BookingsPage from './BookingsPage'
import NetworkDiagramTab from '../components/network/NetworkDiagramTab'

function DeliveryDocAttachModal({ docId, docLabel, onClose }: { docId: string; docLabel: string; onClose: () => void }) {
  const qc = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  const { data: doc, isLoading } = useQuery<any>({
    queryKey: ['delivery', docId],
    queryFn: () => api.get(`/deliveries/${docId}`).then(r => r.data),
  })

  const attachments: any[] = doc?.attachments || []

  const deleteAttach = useMutation({
    mutationFn: (attId: string) => api.delete(`/deliveries/${docId}/attachments/${attId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['delivery', docId] }),
  })

  const handleUpload = async (files: FileList | null) => {
    if (!files?.length) return
    setUploading(true)
    const fd = new FormData()
    Array.from(files).forEach(f => fd.append('files', f))
    try {
      await api.post(`/deliveries/${docId}/attachments`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      qc.invalidateQueries({ queryKey: ['delivery', docId] })
    } catch (e: any) {
      alert(e.response?.data?.message || 'อัปโหลดไม่สำเร็จ')
    }
    setUploading(false)
  }

  const isPdf = (m: string | null) => m === 'application/pdf'
  const fmtSize = (s: number | null) => !s ? '' : s < 1024 * 1024 ? `${(s / 1024).toFixed(0)} KB` : `${(s / 1024 / 1024).toFixed(1)} MB`

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h2 className="font-semibold text-gray-900 flex items-center gap-2"><Paperclip size={15}/> ไฟล์แนบใบส่งมอบ</h2>
            <p className="text-xs text-gray-400 mt-0.5">{docLabel}</p>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={16}/></button>
        </div>

        <div className="p-5 space-y-3">
          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-gray-400 gap-2">
              <RefreshCw size={16} className="animate-spin"/> กำลังโหลด...
            </div>
          ) : attachments.length === 0 ? (
            <div className="flex flex-col items-center py-8 text-gray-300">
              <File size={36} className="mb-2"/>
              <p className="text-sm">ยังไม่มีไฟล์แนบ</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              {attachments.map((att: any) => (
                <div key={att.id} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg group text-xs">
                  <div className={`w-6 h-6 rounded flex items-center justify-center flex-shrink-0 ${isPdf(att.mimeType) ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'}`}>
                    {isPdf(att.mimeType) ? <File size={12}/> : <ImageIcon size={12}/>}
                  </div>
                  <span className="flex-1 truncate text-gray-700">{att.originalName}</span>
                  <span className="text-gray-400 flex-shrink-0">{fmtSize(att.size)}</span>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <a href={att.url} target="_blank" rel="noreferrer" className="p-1 text-gray-400 hover:text-blue-600 rounded"><ExternalLink size={12}/></a>
                    <button onClick={() => { if (confirm('ลบไฟล์นี้?')) deleteAttach.mutate(att.id) }} className="p-1 text-gray-400 hover:text-red-600 rounded"><X size={12}/></button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <input ref={fileRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden"
            onChange={e => handleUpload(e.target.files)}/>
          <button onClick={() => fileRef.current?.click()} disabled={uploading}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 border-2 border-dashed border-gray-200 rounded-xl text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-50">
            {uploading
              ? <><RefreshCw size={14} className="animate-spin"/> กำลังอัปโหลด...</>
              : <><Upload size={14}/> แนบไฟล์ (PDF / รูปภาพ)</>}
          </button>
        </div>

        <div className="p-4 border-t border-gray-100">
          <button onClick={onClose} className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-600 hover:bg-gray-50">ปิด</button>
        </div>
      </div>
    </div>
  )
}

// ── Configurable list columns ────────────────────────────────────────────────
// Every column the Assets table can show. `sort` = backend sortBy key (must be in SORTABLE in
// backend/src/routes/assets.ts). Which columns are visible, and in what order, is saved per user in
// localStorage (see loadColumnPrefs) — "Asset Code" is always shown first.
type ColKey =
  | 'assetCode' | 'name' | 'assetTag' | 'type' | 'category' | 'brand' | 'model' | 'serialNumber'
  | 'ipAddress' | 'macEthernet' | 'macWifi' | 'company' | 'site' | 'department' | 'location'
  | 'status' | 'assignedTo' | 'employeeId' | 'os' | 'cpu' | 'ram' | 'purchaseDate' | 'warrantyExpiry'
  | 'contractEnd' | 'purchasePrice' | 'costCenter' | 'vendor' | 'deliveryDocs' | 'maintenance'
  | 'notes' | 'updatedAt'

const COLUMN_DEFS: { key: ColKey; label: string; sort?: string; group: string }[] = [
  { key:'assetCode',     label:'Asset Code',          sort:'assetCode',      group:'พื้นฐาน' },
  { key:'name',          label:'ชื่อ',                 sort:'name',           group:'พื้นฐาน' },
  { key:'assetTag',      label:'รหัสทรัพย์สิน',         sort:'assetTag',       group:'พื้นฐาน' },
  { key:'type',          label:'ประเภท',               sort:'type',           group:'พื้นฐาน' },
  { key:'category',      label:'หมวดหมู่',             sort:'category',       group:'พื้นฐาน' },
  { key:'brand',         label:'ยี่ห้อ',                sort:'brand',          group:'พื้นฐาน' },
  { key:'model',         label:'รุ่น',                  sort:'model',          group:'พื้นฐาน' },
  { key:'serialNumber',  label:'Serial Number',       sort:'serialNumber',   group:'พื้นฐาน' },
  { key:'status',        label:'สถานะ',                sort:'status',         group:'พื้นฐาน' },
  { key:'ipAddress',     label:'IP Address',          sort:'ipAddress',      group:'เครือข่าย' },
  { key:'macEthernet',   label:'MAC (LAN)',           sort:'macEthernet',    group:'เครือข่าย' },
  { key:'macWifi',       label:'MAC (Wi-Fi)',         sort:'macWifi',        group:'เครือข่าย' },
  { key:'company',       label:'Company',             sort:'company',        group:'ตำแหน่ง' },
  { key:'site',          label:'Site',                sort:'site',           group:'ตำแหน่ง' },
  { key:'department',    label:'Department',          sort:'department',     group:'ตำแหน่ง' },
  { key:'location',      label:'อาคาร/ชั้น/ห้อง',       sort:'building',       group:'ตำแหน่ง' },
  { key:'assignedTo',    label:'ผู้ใช้งาน',             sort:'assignedTo',     group:'ผู้ใช้งาน' },
  { key:'employeeId',    label:'รหัสพนักงาน',          sort:'employeeId',     group:'ผู้ใช้งาน' },
  { key:'os',            label:'OS',                  sort:'os',             group:'Spec' },
  { key:'cpu',           label:'CPU',                 sort:'cpu',            group:'Spec' },
  { key:'ram',           label:'RAM',                 sort:'ram',            group:'Spec' },
  { key:'purchaseDate',  label:'วันที่ซื้อ/เริ่มเช่า',     sort:'purchaseDate',   group:'การเงิน/สัญญา' },
  { key:'warrantyExpiry',label:'หมดประกัน',            sort:'warrantyExpiry', group:'การเงิน/สัญญา' },
  { key:'contractEnd',   label:'สิ้นสุดสัญญา',          sort:'contractEnd',    group:'การเงิน/สัญญา' },
  { key:'purchasePrice', label:'ราคาซื้อ',             sort:'purchasePrice',  group:'การเงิน/สัญญา' },
  { key:'costCenter',    label:'Cost Center',         sort:'costCenter',     group:'การเงิน/สัญญา' },
  { key:'vendor',        label:'Vendor',                                     group:'การเงิน/สัญญา' },
  { key:'deliveryDocs',  label:'ใบส่งมอบ',             sort:'deliveryDocs',   group:'เอกสาร' },
  { key:'maintenance',   label:'MA',                  sort:'maintenance',    group:'เอกสาร' },
  { key:'notes',         label:'หมายเหตุ',             group:'อื่นๆ' },
  { key:'updatedAt',     label:'แก้ไขล่าสุด',           sort:'updatedAt',      group:'อื่นๆ' },
]
const COLUMN_KEYS = new Set<string>(COLUMN_DEFS.map(c => c.key))
const DEFAULT_COLUMNS: ColKey[] = ['assetCode','type','company','site','department','status','assignedTo','purchaseDate','deliveryDocs','maintenance']

const columnPrefsKey = () => {
  let uid = 'anon'
  try { uid = JSON.parse(localStorage.getItem('user') || 'null')?.id || 'anon' } catch { /* ignore */ }
  return `assetListColumns:${uid}`
}
function loadColumnPrefs(): ColKey[] {
  try {
    const raw = JSON.parse(localStorage.getItem(columnPrefsKey()) || 'null')
    if (Array.isArray(raw)) {
      const cols = raw.filter((k: string) => COLUMN_KEYS.has(k) && k !== 'assetCode') as ColKey[]
      return ['assetCode', ...cols]
    }
  } catch { /* ignore */ }
  return DEFAULT_COLUMNS
}
function saveColumnPrefs(cols: ColKey[]) {
  try { localStorage.setItem(columnPrefsKey(), JSON.stringify(cols)) } catch { /* ignore */ }
}

const fmtDate = (d?: string | null) => d
  ? new Date(d).toLocaleDateString('th-TH-u-ca-gregory', { day:'2-digit', month:'2-digit', year:'numeric' })
  : null

function ColumnPicker({ columns, onChange }: { columns: ColKey[]; onChange: (cols: ColKey[]) => void }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const labelOf = (k: ColKey) => COLUMN_DEFS.find(c => c.key === k)?.label || k
  const toggle = (k: ColKey) => onChange(columns.includes(k) ? columns.filter(c => c !== k) : [...columns, k])
  const move = (i: number, d: -1 | 1) => {
    const j = i + d
    if (j < 1 || j >= columns.length) return   // index 0 = Asset Code (fixed)
    const next = [...columns]; [next[i], next[j]] = [next[j], next[i]]; onChange(next)
  }
  const groups = [...new Set(COLUMN_DEFS.map(c => c.group))]

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)}
        className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border rounded-md ${open ? 'border-blue-500 text-blue-700 bg-blue-50' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
        <Columns3 size={14}/> คอลัมน์
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 w-[520px] max-w-[90vw] bg-white border border-gray-200 rounded-lg shadow-xl flex text-xs">
          <div className="flex-1 p-3 border-r border-gray-100 max-h-[60vh] overflow-auto">
            <p className="font-semibold text-gray-700 mb-2">เลือกคอลัมน์ที่แสดง</p>
            {groups.map(g => (
              <div key={g} className="mb-2">
                <p className="text-[10px] uppercase tracking-wide text-gray-400 mb-1">{g}</p>
                {COLUMN_DEFS.filter(c => c.group === g).map(c => (
                  <label key={c.key} className={`flex items-center gap-2 px-1.5 py-1 rounded ${c.key === 'assetCode' ? 'opacity-60' : 'hover:bg-gray-50 cursor-pointer'}`}>
                    <input type="checkbox" checked={columns.includes(c.key)} disabled={c.key === 'assetCode'}
                      onChange={() => toggle(c.key)} className="w-3.5 h-3.5 rounded border-gray-300 text-blue-700"/>
                    <span className="text-gray-700">{c.label}</span>
                  </label>
                ))}
              </div>
            ))}
          </div>
          <div className="w-52 p-3 flex flex-col max-h-[60vh]">
            <p className="font-semibold text-gray-700 mb-2">ลำดับคอลัมน์</p>
            <div className="flex-1 overflow-auto space-y-0.5">
              {columns.map((k, i) => (
                <div key={k} className="flex items-center gap-1 px-1.5 py-1 rounded bg-gray-50">
                  <span className="flex-1 truncate text-gray-700">{labelOf(k)}</span>
                  {i > 0 && <>
                    <button onClick={() => move(i, -1)} disabled={i <= 1} className="p-0.5 text-gray-400 hover:text-blue-600 disabled:opacity-30"><ArrowUp size={12}/></button>
                    <button onClick={() => move(i, 1)} disabled={i >= columns.length - 1} className="p-0.5 text-gray-400 hover:text-blue-600 disabled:opacity-30"><ArrowDown size={12}/></button>
                  </>}
                </div>
              ))}
            </div>
            <button onClick={() => onChange(DEFAULT_COLUMNS)}
              className="mt-2 flex items-center justify-center gap-1 px-2 py-1.5 border border-gray-200 rounded text-gray-600 hover:bg-gray-50">
              <RotateCcw size={12}/> ค่าเริ่มต้น
            </button>
            <p className="text-[10px] text-gray-400 mt-1.5 text-center">บันทึกอัตโนมัติ</p>
          </div>
        </div>
      )}
    </div>
  )
}

function AssetListTab({ isAdmin }: { isAdmin: boolean }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  // init state จาก URL params
  const [search,     setSearch]     = useState(searchParams.get('search') || '')
  const [type,       setType]       = useState(searchParams.get('type') || '')
  const [status,     setStatus]     = useState(searchParams.get('status') || '')
  const [department, setDepartment] = useState(searchParams.get('department') || '')  // stores departmentId
  const [site,       setSite]       = useState(searchParams.get('site') || '')          // stores siteId
  const [company,    setCompany]    = useState(searchParams.get('company') || '')
  const [companyId,  setCompanyId]  = useState(searchParams.get('companyId') || '')
  const [hasDelivery,  setHasDelivery]  = useState(searchParams.get('hasDelivery') || '')
  const [sortBy,     setSortBy]     = useState(searchParams.get('sortBy') || 'updatedAt')
  const [sortOrder,  setSortOrder]  = useState<'asc'|'desc'>((searchParams.get('sortOrder') as any) || 'desc')
  const [attachDoc,  setAttachDoc]  = useState<{id:string;label:string} | null>(null)
  const [columns,    setColumnsState] = useState<ColKey[]>(loadColumnPrefs)
  const setColumns = (cols: ColKey[]) => { setColumnsState(cols); saveColumnPrefs(cols) }

  // sync URL เมื่อ filter เปลี่ยน
  useEffect(() => {
    const p: Record<string,string> = { sortBy, sortOrder }
    if (search)     p.search     = search
    if (type)       p.type       = type
    if (status)     p.status     = status
    if (department) p.department = department
    if (site)       p.site       = site
    if (company)    p.company    = company
    if (companyId)  p.companyId  = companyId
    if (hasDelivery)      p.hasDelivery      = hasDelivery
    setSearchParams(p, { replace: true })
  }, [search, type, status, department, site, company, companyId, hasDelivery, sortBy, sortOrder])

  const params = new URLSearchParams({ page: '1', limit: '2000', sortBy, sortOrder,
    ...(search&&{search}), ...(type&&{type}), ...(status&&{status}),
    ...(department&&{departmentId:department}), ...(site&&{siteId:site}), ...(company&&{company}),
    ...(companyId&&{companyId}),
    ...(hasDelivery&&{hasDelivery}) })

  const { data, isLoading } = useQuery<AssetsResponse>({
    queryKey: ['assets', search, type, status, department, site, company, companyId, hasDelivery, sortBy, sortOrder],
    queryFn: () => api.get(`/assets?${params}`).then(r => r.data),
  })

  // Remember every asset seen so a selection made under one filter can still be printed after the filter changes
  const seenAssets = useRef(new Map<string, Asset>())
  ;(data?.assets || []).forEach(a => seenAssets.current.set(a.id, a))
  const [labelAssets, setLabelAssets] = useState<Asset[] | null>(null)

  const handleSort = (col: string) => {
    if (sortBy === col) { setSortOrder(o => o === 'asc' ? 'desc' : 'asc') }
    else { setSortBy(col); setSortOrder('asc') }
  }
  const SortIcon = ({ col }: { col: string }) => {
    if (sortBy !== col) return <ChevronsUpDown size={12} className="text-gray-300"/>
    return sortOrder === 'asc' ? <ChevronUp size={12} className="text-blue-500"/> : <ChevronDown size={12} className="text-blue-500"/>
  }


  const { data: deliveryDocs = [] } = useQuery<{id:string;assetId:string|null;docNumber:string|null;employeeName:string|null;createdAt:string;docStatus:string|null}[]>({
    queryKey: ['deliveries'],
    queryFn: () => api.get('/deliveries').then(r => r.data).catch(() => []),
  })
  const deliveryMap = deliveryDocs.reduce((acc, d) => {
    if (d.assetId) { if (!acc[d.assetId]) acc[d.assetId] = []; acc[d.assetId].push(d) }
    return acc
  }, {} as Record<string, typeof deliveryDocs>)

  const { data: assetTypes = [] }    = useQuery<{id:string;label:string;value:string}[]>({ queryKey: ['masterdata','asset-types'], queryFn: () => api.get('/masterdata/asset-types').then(r=>r.data).catch(()=>[]) })
  const { data: assetStatuses = [] } = useQuery<{id:string;label:string;value:string}[]>({ queryKey: ['masterdata','asset-statuses'], queryFn: () => api.get('/masterdata/asset-statuses').then(r=>r.data).catch(()=>[]) })
  const { data: companies = [] } = useQuery<{id:string;name:string}[]>({ queryKey: ['masterdata','companies'],    queryFn: () => api.get('/masterdata/companies').then(r=>r.data).catch(()=>[]) })
  const { data: allSites = [] }  = useQuery<{id:string;name:string;companyId?:string}[]>({ queryKey: ['masterdata','sites'], queryFn: () => api.get('/masterdata/sites').then(r=>r.data).catch(()=>[]) })
  const { data: allDepts = [] }  = useQuery<{id:string;name:string;siteId?:string}[]>({ queryKey: ['masterdata','departments'], queryFn: () => api.get('/masterdata/departments').then(r=>r.data).catch(()=>[]) })
  const companyMap  = Object.fromEntries(companies.map(c => [c.id, c.name]))
  const siteMap     = Object.fromEntries(allSites.map(s => [s.id, s.name]))
  const deptMap     = Object.fromEntries(allDepts.map(d => [d.id, d.name]))
  const typeLabelMap   = Object.fromEntries(assetTypes.map(t => [t.value, t.label]))
  const statusLabelMap = Object.fromEntries(assetStatuses.map(s => [s.value, s.label]))

  const setFilter = (key: string, val: string) => {
    if (key === 'search')     setSearch(val)
    if (key === 'type')       setType(val)
    if (key === 'status')     setStatus(val)
    if (key === 'department') setDepartment(val)
    if (key === 'site')       setSite(val)
    if (key === 'company')    setCompany(val)
    if (key === 'companyId')  setCompanyId(val)
    if (key === 'hasDelivery')    setHasDelivery(val)
  }

  // cascade: เมื่อเปลี่ยน company → clear site & dept; เมื่อเปลี่ยน site → clear dept
  const handleCompanyChange = (val: string) => { setCompanyId(val); setSite(''); setDepartment('') }
  const handleSiteChange    = (val: string) => { setSite(val); setDepartment('') }

  const filteredSites = companyId ? allSites.filter(s => s.companyId === companyId) : allSites
  const selectedSiteObj = allSites.find(s => s.id === site)
  const filteredDepts = selectedSiteObj ? allDepts.filter(d => d.siteId === selectedSiteObj.id) : []

  const clearAll = () => {
    setSearch(''); setType(''); setStatus(''); setDepartment(''); setSite(''); setCompany(''); setCompanyId('')
    setHasDelivery('')
  }
  const hasFilter = search || type || status || department || site || company || companyId || hasDelivery

  const handleExport = async () => {
    const ids = [...selectedIds]
    const url = ids.length > 0 ? `/api/assets/export/xlsx?ids=${ids.join(',')}` : '/api/assets/export/xlsx'
    const activeCompanyId = localStorage.getItem('activeCompanyId')
    const headers: Record<string, string> = { Authorization: `Bearer ${localStorage.getItem('token')}` }
    if (activeCompanyId) headers['X-Company-Id'] = activeCompanyId
    const res = await fetch(url, { headers })
    const blob = await res.blob()
    const objUrl = URL.createObjectURL(blob)
    const a = document.createElement('a'); a.href = objUrl; a.download = 'assets.xlsx'; a.click()
    URL.revokeObjectURL(objUrl)
  }

  const bulkDeleteMut = useMutation({
    mutationFn: (ids: string[]) => api.delete('/assets/bulk', { data: { ids } }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['assets'] }); setSelectedIds(new Set()) },
    onError: (e: any) => alert(e.response?.data?.message || 'เกิดข้อผิดพลาด'),
  })

  const currentPageIds = (data?.assets || []).map(a => a.id)
  const allSelected = currentPageIds.length > 0 && currentPageIds.every(id => selectedIds.has(id))
  const someSelected = currentPageIds.some(id => selectedIds.has(id))

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(prev => { const s = new Set(prev); currentPageIds.forEach(id => s.delete(id)); return s })
    } else {
      setSelectedIds(prev => new Set([...prev, ...currentPageIds]))
    }
  }
  const toggleOne = (id: string) => {
    setSelectedIds(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s })
  }

  const handleBulkDelete = () => {
    const ids = [...selectedIds]
    if (!confirm(`ลบ ${ids.length} รายการที่เลือก?`)) return
    bulkDeleteMut.mutate(ids)
  }

  const textCell = (val: any, cls = 'max-w-[160px]') => (
    <td className={`px-3 py-1.5 text-gray-600 text-xs truncate ${cls}`} title={val ? String(val) : ''}>
      {val || <span className="text-gray-300">—</span>}
    </td>
  )
  const dateCell = (d?: string | null) => (
    <td className="px-3 py-1.5 text-gray-500 text-xs whitespace-nowrap">{fmtDate(d) || <span className="text-gray-300">—</span>}</td>
  )

  const renderCell = (key: ColKey, asset: any) => {
    switch (key) {
      case 'assetCode': return (
        <td className="px-3 py-1.5">
          <div className="flex items-center gap-2">
            {asset.assetCode && <span className="text-xs font-mono bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-semibold border border-blue-200">{asset.assetCode}</span>}
            <DataSourceBadge source={asset.dataSource} />
          </div>
        </td>
      )
      case 'name':         return textCell(asset.name, 'max-w-[200px]')
      case 'assetTag':     return textCell(asset.assetTag, 'max-w-[140px] font-mono')
      case 'type':         return <td className="px-3 py-1.5 text-gray-600 whitespace-nowrap text-xs">{typeLabelMap[asset.type] || asset.type}</td>
      case 'category':     return textCell(asset.category, 'max-w-[140px]')
      case 'brand':        return textCell(asset.brand, 'max-w-[120px]')
      case 'model':        return textCell(asset.model, 'max-w-[160px]')
      case 'serialNumber': return textCell(asset.serialNumber, 'max-w-[180px] font-mono')
      case 'ipAddress':    return textCell(asset.ipAddress, 'max-w-[140px] font-mono whitespace-nowrap')
      case 'macEthernet':  return textCell(asset.macEthernet, 'max-w-[150px] font-mono whitespace-nowrap')
      case 'macWifi':      return textCell(asset.macWifi, 'max-w-[150px] font-mono whitespace-nowrap')
      case 'company':      return textCell(companyMap[asset.companyId] || asset.company, 'max-w-[140px]')
      case 'site':         return textCell(siteMap[asset.siteId] || asset.site, 'max-w-[120px]')
      case 'department':   return textCell(deptMap[asset.departmentId] || asset.department, 'max-w-[160px]')
      case 'location':     return textCell([asset.building, asset.floor && `ชั้น ${asset.floor}`, asset.room].filter(Boolean).join(' / '), 'max-w-[160px]')
      case 'status': return (
        <td className="px-3 py-1.5">
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${STATUS_COLORS[asset.status] || 'bg-gray-100 text-gray-600'}`}>{statusLabelMap[asset.status] || asset.status}</span>
        </td>
      )
      case 'assignedTo':   return textCell(asset.assignedTo, 'max-w-[140px]')
      case 'employeeId':   return textCell(asset.employeeId, 'max-w-[120px]')
      case 'os':           return textCell(asset.os, 'max-w-[160px]')
      case 'cpu':          return textCell(asset.cpu, 'max-w-[180px]')
      case 'ram':          return textCell(asset.ram, 'max-w-[100px]')
      case 'purchaseDate':   return dateCell(asset.purchaseDate)
      case 'warrantyExpiry': {
        const expired = asset.warrantyExpiry && new Date(asset.warrantyExpiry) < new Date()
        return (
          <td className={`px-3 py-1.5 text-xs whitespace-nowrap ${expired ? 'text-red-600' : 'text-gray-500'}`}>
            {fmtDate(asset.warrantyExpiry) || <span className="text-gray-300">—</span>}
          </td>
        )
      }
      case 'contractEnd':   return dateCell(asset.contractEnd)
      case 'purchasePrice': return (
        <td className="px-3 py-1.5 text-gray-600 text-xs whitespace-nowrap text-right tabular-nums">
          {asset.purchasePrice != null ? Number(asset.purchasePrice).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : <span className="text-gray-300">—</span>}
        </td>
      )
      case 'costCenter':    return textCell(asset.costCenter, 'max-w-[120px]')
      case 'vendor':        return textCell(asset.vendor?.name, 'max-w-[160px]')
      case 'notes':         return textCell(asset.notes, 'max-w-[220px]')
      case 'updatedAt':     return dateCell(asset.updatedAt)
      case 'deliveryDocs': return (
        <td className="px-3 py-1.5" onClick={e => e.stopPropagation()}>
          {(() => {
            const assignedTo = asset.assignedTo
            if (!assignedTo) return <span className="text-xs text-gray-300">—</span>
            const latestDoc = (deliveryMap[asset.id] || [])
              .slice()
              .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0]
            const hasDoc = latestDoc?.employeeName === assignedTo && latestDoc?.docStatus !== 'CANCELLED'
            return hasDoc ? (
              <div className="flex items-center gap-1">
                <span className="flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded font-medium">
                  <ClipboardList size={11}/> ใบส่งมอบ
                </span>
                <button onClick={() => setAttachDoc({ id: latestDoc.id, label: latestDoc.docNumber || latestDoc.id })}
                  className="p-1 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors" title="แนบไฟล์ใบส่งมอบ">
                  <Paperclip size={12}/>
                </button>
                <button onClick={() => window.open(`/deliveries/${latestDoc.id}/print`, '_blank')}
                  className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors" title="พิมพ์ใบส่งมอบ">
                  <Printer size={13}/>
                </button>
              </div>
            ) : (
              <span className="flex items-center gap-1 text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded font-medium">
                <ClipboardList size={11}/> ไม่มีใบส่งมอบ
              </span>
            )
          })()}
        </td>
      )
      case 'maintenance': return (
        <td className="px-3 py-1.5">
          {(asset._count?.maintenance || 0) > 0 ? (
            <div className="flex flex-col gap-0.5">
              <span className="flex items-center gap-1 text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded font-medium w-fit">
                <Wrench size={11}/> MA แล้ว ({asset._count.maintenance})
              </span>
              {asset.maintenance?.[0]?.date && (
                <span className="text-[11px] text-gray-400 pl-0.5">ล่าสุด {fmtDate(asset.maintenance[0].date)}</span>
              )}
            </div>
          ) : (
            <span className="flex items-center gap-1 text-xs bg-gray-50 text-gray-500 border border-gray-200 px-2 py-0.5 rounded font-medium w-fit">
              <Wrench size={11}/> ยังไม่มี MA
            </span>
          )}
        </td>
      )
    }
  }

  return (
    <div className="h-full flex flex-col gap-3 p-4 overflow-hidden">
      <div className="flex items-center justify-between gap-3 pb-3 border-b border-gray-200">
        <div>
          <h1 className="text-lg font-semibold text-gray-900 tracking-tight">Assets</h1>
          <p className="text-xs text-gray-500 mt-0.5">ทะเบียนทรัพย์สินและอุปกรณ์ IT</p>
        </div>
        <div className="flex gap-2 items-center">
          {isAdmin && selectedIds.size > 0 && (
            <button onClick={handleBulkDelete} disabled={bulkDeleteMut.isPending}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50">
              <Trash2 size={14}/> ลบที่เลือก ({selectedIds.size})
            </button>
          )}
          {selectedIds.size > 0 && (
            <button onClick={() => setLabelAssets([...selectedIds].map(id => seenAssets.current.get(id)).filter((a): a is Asset => !!a))}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
              <QrCode size={14}/> พิมพ์ป้าย QR ({selectedIds.size})
            </button>
          )}
          <ColumnPicker columns={columns} onChange={setColumns}/>
          <button onClick={handleExport} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50">
            <Download size={14}/> Export{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
          </button>
          {isAdmin && <Link to="/assets/new" className="flex items-center gap-1.5 px-4 py-2 bg-blue-700 text-white rounded-md text-sm font-medium hover:bg-blue-800"><Plus size={14}/> เพิ่ม Asset</Link>}
        </div>
      </div>

      {/* Filters — one compact row */}
      <div className="bg-white rounded-md border border-gray-200 px-2.5 py-2 flex items-center gap-1.5 flex-wrap">
        <div className="relative w-40 flex-shrink-0">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400"/>
          <input value={search} onChange={e => setFilter('search', e.target.value)} placeholder="ค้นหา asset, serial, IP..." className="w-full pl-6 pr-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600"/>
        </div>
        <select value={type} onChange={e => setFilter('type', e.target.value)} className="border border-gray-300 rounded px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 bg-white">
          <option value="">ทุกประเภท</option>
          {assetTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select value={status} onChange={e => setFilter('status', e.target.value)} className="border border-gray-300 rounded px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 bg-white">
          <option value="">ทุกสถานะ</option>
          {assetStatuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <select value={companyId} onChange={e => handleCompanyChange(e.target.value)} className="border border-gray-300 rounded px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 bg-white">
          <option value="">ทุกบริษัท</option>
          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={site} onChange={e => handleSiteChange(e.target.value)} className="border border-gray-300 rounded px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 bg-white" disabled={filteredSites.length === 0}>
          <option value="">ทุก Site</option>
          {filteredSites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={department} onChange={e => setFilter('department', e.target.value)} disabled={!site} className="border border-gray-300 rounded px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 bg-white disabled:opacity-50 disabled:cursor-not-allowed">
          <option value="">{site ? 'ทุก Department' : 'เลือก Site ก่อน'}</option>
          {filteredDepts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select value={hasDelivery} onChange={e => setFilter('hasDelivery', e.target.value)} className="border border-gray-300 rounded px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 bg-white">
          <option value="">ใบส่งมอบทั้งหมด</option>
          <option value="true">มีใบส่งมอบ</option>
          <option value="false">ไม่มีใบส่งมอบ</option>
        </select>
        <div className="ml-auto flex items-center gap-2.5 flex-shrink-0">
          <p className="text-xs text-gray-500 whitespace-nowrap">{data?.total ?? 0} รายการ</p>
          {hasFilter && <button onClick={clearAll} className="text-xs text-blue-700 hover:underline whitespace-nowrap">ล้างตัวกรอง</button>}
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 min-h-0 bg-white rounded-md border border-gray-200 overflow-hidden flex flex-col">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-400 text-sm">กำลังโหลด...</div>
        ) : (data?.assets || []).length === 0 ? (
          <div className="flex flex-col items-center py-16 text-gray-400 gap-2">
            <Package size={32} className="text-gray-200"/>
            <p className="text-sm">ไม่พบ Asset</p>
          </div>
        ) : (
          <div className="overflow-auto flex-1 min-h-0">
            <table className="w-full text-sm">
              <thead className="bg-gray-100 border-b border-gray-200 sticky top-0 z-10">
                <tr>
                  {isAdmin && (
                    <th className="px-3 py-2.5 w-10">
                      <input type="checkbox" checked={allSelected} ref={el => { if (el) el.indeterminate = someSelected && !allSelected }}
                        onChange={toggleAll} className="w-4 h-4 rounded border-gray-300 text-blue-700 cursor-pointer"/>
                    </th>
                  )}
                  {columns.map(key => {
                    const def = COLUMN_DEFS.find(c => c.key === key)!
                    return (
                      <th key={key}
                        onClick={() => def.sort && handleSort(def.sort)}
                        className={`text-left px-3 py-2.5 text-[11px] font-semibold text-gray-600 uppercase tracking-wide select-none whitespace-nowrap ${def.sort ? 'cursor-pointer hover:bg-gray-200/60 hover:text-gray-800' : ''}`}>
                        <div className="flex items-center gap-1">
                          {def.label}
                          {def.sort && <SortIcon col={def.sort}/>}
                        </div>
                      </th>
                    )
                  })}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(data?.assets || []).map(asset => (
                  <tr key={asset.id} onClick={() => navigate(`/assets/${asset.id}`)}
                    className={`hover:bg-blue-50 cursor-pointer transition-colors ${selectedIds.has(asset.id) ? 'bg-blue-50' : ''}`}>
                    {isAdmin && (
                      <td className="px-3 py-1.5 w-10" onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={selectedIds.has(asset.id)} onChange={() => toggleOne(asset.id)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-700 cursor-pointer"/>
                      </td>
                    )}
                    {columns.map(key => <Fragment key={key}>{renderCell(key, asset)}</Fragment>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {labelAssets && <QRCodeModal assets={labelAssets} onClose={() => setLabelAssets(null)}/>}

      {attachDoc && (
        <DeliveryDocAttachModal
          docId={attachDoc.id}
          docLabel={attachDoc.label}
          onClose={() => setAttachDoc(null)}
        />
      )}
    </div>
  )
}

// Wrapper: "Assets" (list) + "การยืม" (Bookings, moved here from its own menu item) + "Network Diagram"
// as tabs. `isAdmin` here means "can manage assets" (used by the list tab and to edit diagrams);
// `isFullAdmin` is the real ADMIN/SUPER_ADMIN role, used by the Bookings tab's approve/reject controls.
// `allowedMenus` (from Settings → การเข้าถึงเมนู) still hides the Bookings / Network Diagram tabs for
// roles without that permission.
type AssetsTab = 'list' | 'bookings' | 'diagram'
export default function AssetsPage({ isAdmin, isFullAdmin, allowedMenus }: { isAdmin: boolean; isFullAdmin: boolean; allowedMenus?: string[] | null }) {
  const [searchParams] = useSearchParams()
  const showBookingsTab = isFullAdmin || allowedMenus == null || allowedMenus.includes('bookings')
  const showDiagramTab = isFullAdmin || allowedMenus == null || allowedMenus.includes('networkdiagram')
  const [tab, setTab] = useState<AssetsTab>(() => {
    const t = searchParams.get('tab')
    return t === 'bookings' || t === 'diagram' ? t : 'list'
  })
  const tabCls = (t: AssetsTab) => `flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-800'}`

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex gap-1 px-4 pt-3 border-b border-gray-200 bg-white flex-shrink-0" role="tablist">
        <button role="tab" aria-selected={tab === 'list'} onClick={() => setTab('list')} className={tabCls('list')}>
          <Package size={14}/> Assets
        </button>
        {showBookingsTab && (
          <button role="tab" aria-selected={tab === 'bookings'} onClick={() => setTab('bookings')} className={tabCls('bookings')}>
            <ArrowLeftRight size={14}/> การยืม
          </button>
        )}
        {showDiagramTab && (
          <button role="tab" aria-selected={tab === 'diagram'} onClick={() => setTab('diagram')} className={tabCls('diagram')}>
            <Network size={14}/> Network Diagram
          </button>
        )}
      </div>
      {tab === 'bookings' && showBookingsTab ? (
        <div className="flex-1 min-h-0 overflow-auto"><BookingsPage isAdmin={isFullAdmin}/></div>
      ) : tab === 'diagram' && showDiagramTab ? (
        <div className="flex-1 min-h-0 overflow-hidden"><NetworkDiagramTab canEdit={isAdmin}/></div>
      ) : (
        <div className="flex-1 min-h-0 overflow-hidden"><AssetListTab isAdmin={isAdmin}/></div>
      )}
    </div>
  )
}
